import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { ClientLicense, ClientLicenseFormData } from '@/types/license';

export const useClientLicenses = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    data: clientLicenses = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ['client-licenses'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('client_licenses')
        .select(`
          *,
          license_plan:license_plans(*),
          client:clients(id, name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as ClientLicense[];
    }
  });

  const addClientLicenseMutation = useMutation({
    mutationFn: async (licenseData: ClientLicenseFormData) => {
      const { data, error } = await supabase
        .from('client_licenses')
        .insert([licenseData])
        .select(`
          *,
          license_plan:license_plans(*),
          client:clients(id, name)
        `)
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-licenses'] });
      toast({
        title: "Licença atribuída",
        description: "Licença atribuída ao cliente com sucesso.",
      });
    },
    onError: (error: any) => {
      console.error('Error creating client license:', error);
      toast({
        title: "Erro ao atribuir licença",
        description: "Não foi possível atribuir a licença ao cliente.",
        variant: "destructive",
      });
    }
  });

  const updateClientLicenseMutation = useMutation({
    mutationFn: async ({ id, licenseData }: { id: string; licenseData: Partial<ClientLicenseFormData> }) => {
      // Extract only valid DB fields
      const { client_id, license_plan_id, ...dbData } = licenseData as any;
      const { data, error } = await supabase
        .from('client_licenses')
        .update(dbData)
        .eq('id', id)
        .select(`
          *,
          license_plan:license_plans(*),
          client:clients(id, name)
        `)
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-licenses'] });
      toast({
        title: "Licença atualizada",
        description: "Licença do cliente atualizada com sucesso.",
      });
    },
    onError: (error: any) => {
      console.error('Error updating client license:', error);
      toast({
        title: "Erro ao atualizar licença",
        description: "Não foi possível atualizar a licença do cliente.",
        variant: "destructive",
      });
    }
  });

  const suspendClientLicenseMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from('client_licenses')
        .update({ status: 'suspended' })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-licenses'] });
      toast({
        title: "Licença suspensa",
        description: "Licença suspensa com sucesso.",
      });
    },
    onError: (error: any) => {
      console.error('Error suspending license:', error);
      toast({
        title: "Erro ao suspender licença",
        description: "Não foi possível suspender a licença.",
        variant: "destructive",
      });
    }
  });

  const activateClientLicenseMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from('client_licenses')
        .update({ status: 'active' })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-licenses'] });
      toast({
        title: "Licença ativada",
        description: "Licença ativada com sucesso.",
      });
    },
    onError: (error: any) => {
      console.error('Error activating license:', error);
      toast({
        title: "Erro ao ativar licença",
        description: "Não foi possível ativar a licença.",
        variant: "destructive",
      });
    }
  });

  return {
    clientLicenses,
    isLoading,
    error,
    addClientLicense: addClientLicenseMutation.mutate,
    updateClientLicense: updateClientLicenseMutation.mutate,
    suspendClientLicense: suspendClientLicenseMutation.mutate,
    activateClientLicense: activateClientLicenseMutation.mutate,
    isAddingLicense: addClientLicenseMutation.isPending,
    isUpdatingLicense: updateClientLicenseMutation.isPending,
    isSuspendingLicense: suspendClientLicenseMutation.isPending,
    isActivatingLicense: activateClientLicenseMutation.isPending
  };
};